import React, { useState } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  IconButton, 
  Chip, 
  Collapse, 
  Button,
  Fade,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { 
  Close as CloseIcon, 
  ExpandMore as ExpandMoreIcon,
  NotificationsActive as NotificationsActiveIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

interface NotificationCardProps {
  id: string;
  message: string;
  category: string;
  created_at: string;
  is_read: boolean;
  onClose: (id: string) => Promise<void>;
  onClick?: (id: string) => void;
  referenceId?: string;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  id,
  message,
  category,
  created_at,
  is_read,
  onClose,
  onClick,
  referenceId
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const theme = useTheme();

  // Get category-based styles
  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'urgent':
        return { light: theme.palette.error.light, main: theme.palette.error.main };
      case 'important':
        return { light: theme.palette.warning.light, main: theme.palette.warning.main };
      case 'collection':
        return { light: theme.palette.primary.light, main: theme.palette.primary.main };
      default:
        return { light: theme.palette.info.light, main: theme.palette.info.main };
    }
  };

  const categoryColor = getCategoryColor(category);
  const formattedDate = format(new Date(created_at), 'MMM dd, yyyy h:mm a');
  const timeAgo = formatDistanceToNow(new Date(created_at), { addSuffix: true });

  // Format the message to handle potentially long text and hide ID
  const formatMessage = () => {
    // Remove the ID part from the message
    const messageWithoutId = message.replace(/\s*\(ID:[^)]+\)/, '');
    
    if (messageWithoutId.length > 120 && !expanded) {
      return `${messageWithoutId.substring(0, 120)}...`;
    }
    return messageWithoutId;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // If clicking on close button or expand button, don't trigger the card click
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (onClick && referenceId) {
      onClick(id);
    } else {
      setExpanded(!expanded);
    }
  };

  const handleClose = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      setIsClosing(true);
      await onClose(id);
    } catch (error) {
      console.error('Error closing notification:', error);
      setIsClosing(false);
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Paper
        elevation={isHovered ? 2 : 0}
        sx={{
          width: '100%',
          mb: 1.5,
          overflow: 'hidden',
          borderRadius: 2,
          border: '1px solid',
          borderColor: is_read ? 'divider' : `${categoryColor.main}30`,
          bgcolor: is_read ? 'white' : `${categoryColor.light}10`,
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          cursor: onClick && referenceId ? 'pointer' : 'default',
          '&:hover': {
            borderColor: is_read ? 'divider' : categoryColor.main,
            transform: onClick && referenceId ? 'translateY(-2px)' : 'none',
            boxShadow: is_read ? 1 : 2,
          },
          '&::before': !is_read ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            bgcolor: categoryColor.main,
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
          } : {},
          opacity: isClosing ? 0.6 : 1,
          pointerEvents: isClosing ? 'none' : 'auto',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        <Box sx={{ p: 2 }}>
          {/* Header with category and close button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={category}
                size="small"
                icon={!is_read ? <NotificationsActiveIcon fontSize="small" /> : undefined}
                sx={{
                  bgcolor: `${categoryColor.main}15`,
                  color: categoryColor.main,
                  fontWeight: 'medium',
                  fontSize: '0.75rem',
                  height: '24px',
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                  '& .MuiChip-icon': {
                    ml: 0.5,
                    mr: -0.5
                  }
                }}
              />
              <Tooltip title={formattedDate}>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5 }}>
                  <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {timeAgo}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
            
            <Tooltip title="Dismiss notification">
              <span>
                <IconButton
                  aria-label="close notification"
                  size="small"
                  onClick={handleClose}
                  disabled={isClosing}
                  sx={{
                    width: 26,
                    height: 26,
                    opacity: isHovered ? 0.9 : 0.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: `${categoryColor.main}20`,
                      transform: 'scale(1.1)',
                    },
                    zIndex: 2,
                  }}
                >
                  {isClosing ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <CloseIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Message content */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: is_read ? 'normal' : 'medium',
              mb: 1.5,
              color: 'text.primary',
              lineHeight: 1.5
            }}
          >
            {formatMessage()}
          </Typography>

          {/* Footer with expand button only if message is long */}
          {message.length > 120 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                size="small"
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  minWidth: 0,
                  p: 0.5,
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: categoryColor.main,
                  },
                }}
              >
                {expanded ? 'Show less' : 'Read more'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Expanded content */}
        <Collapse in={expanded} timeout="auto">
          <Box sx={{ px: 2, pb: 2, pt: 0, borderTop: expanded ? `1px dashed ${theme.palette.divider}` : 'none', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
};

export default NotificationCard; 